import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  getAnnotatorPharmacies,
  getAnnotatorPrescriptions,
} from '../apis/annotatorView'
import { getPrescriptionByRequestId } from '../apis/prescription'
import { Prescription } from '../../../types/models/Presc'

/**
 * annotator の閲覧（latest UI）のデータ源。
 *
 * 顧客側の usePrescriptionsQuery は**呼ばない**。あちらは 2 秒ポーリング + 差分マージで、
 * 見るだけの面には要らない（薬局を切り替えるたびに全件取り直すほうが素直）。
 */

const STALE_MS = 30_000

/**
 * has_more を落とさずに丸ごと返す。候補が打ち切られたことを画面で伝えるため
 * （「候補が無い」と「上限で切れた」を取り違えさせない）。
 */
export const useAnnotatorPharmaciesQuery = (keyword: string) =>
  useQuery({
    queryKey: ['annotator', 'pharmacies', keyword],
    queryFn: async () => {
      const { data } = await getAnnotatorPharmacies(keyword)
      return data
    },
    staleTime: STALE_MS,
  })

export const useAnnotatorPrescriptionsQuery = (pharmacyId: string | null) =>
  useQuery({
    queryKey: ['annotator', 'prescriptions', pharmacyId],
    queryFn: async () => {
      if (!pharmacyId) return null
      const { data } = await getAnnotatorPrescriptions(pharmacyId)
      return data
    },
    enabled: !!pharmacyId,
    staleTime: STALE_MS,
  })

type RequestIdLookup = {
  /** 直近に ID で引けた紙。開いているかどうかは App の openId が決める */
  found: Prescription | null
  error: string
  isSearching: boolean
  /** 引けた紙（失敗なら null）。失敗しても found は消さない（開いている紙を閉じない） */
  lookup: (requestId: string) => Promise<Prescription | null>
  clear: () => void
}

/**
 * リクエストID 1 本から処方箋を引く。**薬局を選ばずに飛べる**のがこの面の要点なので、
 * 一覧とは独立した状態として持つ。「どの紙を開いているか」はここでは持たない（App の openId）。
 */
export const useRequestIdLookup = (): RequestIdLookup => {
  const [found, setFound] = useState<Prescription | null>(null)
  const [error, setError] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  const lookup = async (requestId: string): Promise<Prescription | null> => {
    const trimmed = requestId.trim()
    if (!trimmed) return null

    setIsSearching(true)
    setError('')
    try {
      const { data } = await getPrescriptionByRequestId(trimmed)
      setFound(data)
      return data
    } catch {
      setError('該当する処方箋が見つかりません')
      return null
    } finally {
      setIsSearching(false)
    }
  }

  const clear = () => {
    setFound(null)
    setError('')
  }

  return { found, error, isSearching, lookup, clear }
}
